import { ArrowRight, Repeat } from "lucide-react";
import { PreSaleRedo, PreSaleRedoProduct } from "@/Api/preSales";
import { cn } from "@/lib/utils";
import { Mask } from "@/utils/mask";

interface PreSaleRedoSummaryProps {
  redo: PreSaleRedo;
  /** Classes extras do container */
  className?: string;
}

const ProductChip = ({ product, muted }: { product: PreSaleRedoProduct; muted?: boolean }) => (
  <div
    className={cn(
      "flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-3 py-2 text-white",
      muted && "opacity-60 line-through decoration-white/70"
    )}
    style={{ backgroundColor: product.lottery?.color || "#64748b" }}
  >
    <div className="min-w-0">
      <p className="truncate text-lg font-bold leading-tight">{product.product_name || "-"}</p>
      {product.lottery && <p className="truncate text-xs opacity-90">{product.lottery.name}</p>}
    </div>
    <span className="shrink-0 text-sm font-medium">{Mask.formatCurrency(product.price_unit)}</span>
  </div>
);

/**
 * LOT-51: o que foi trocado no Refazer (de -> para), com a observação de quem refez.
 * Compartilhado entre o modal de detalhes e o aviso ao vendedor.
 */
export function PreSaleRedoSummary({ redo, className }: PreSaleRedoSummaryProps) {
  return (
    <div
      className={cn(
        "space-y-3 rounded-lg border border-amber-200 bg-amber-50/70 p-4 dark:border-amber-900/40 dark:bg-amber-950/20",
        className
      )}
    >
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
          <Repeat className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <h4 className="text-sm font-medium">Alteração no pedido (Refazer)</h4>
          {redo.requested_by && (
            <p className="truncate text-xs text-muted-foreground">
              Por {redo.requested_by.name} em{" "}
              {new Date(redo.created_at).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {redo.items.map((item) => (
          <div key={item.id} className="flex flex-col items-stretch gap-1.5 sm:flex-row sm:items-center sm:gap-2">
            <ProductChip product={item.from} muted />
            <ArrowRight className="mx-auto h-4 w-4 shrink-0 rotate-90 text-amber-700 sm:rotate-0 dark:text-amber-300" />
            <ProductChip product={item.to} />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between rounded-md bg-white px-3 py-2 text-sm shadow-sm dark:bg-gray-800">
        <span className="text-muted-foreground">Valor final (não mudou)</span>
        <span className="font-semibold text-emerald-700 dark:text-emerald-300">
          {Mask.formatCurrency(redo.total)}
        </span>
      </div>

      {redo.notes && (
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Observação</p>
          <p className="whitespace-pre-wrap break-words text-sm">{redo.notes}</p>
        </div>
      )}
    </div>
  );
}
